import React, { useRef, useState } from 'react';
import { DeleteOutlined, PictureOutlined, SyncOutlined } from '@ant-design/icons';
import ImageCropModal from '../../commonComponents/ImageCropModal';
import { notification } from '../../commonComponents/Notification/Notification';
import { ImageUploadFieldProps } from './types';
import { IMAGE_FILE_HINT, MAX_IMAGE_SIZE_BYTES, DEFAULT_PICTURE } from './constants';
import {
  DeleteImageButton,
  HiddenFileInput,
  ImageActionButtons,
  ImageEmptyIcon,
  ImageEmptyState,
  ImageEmptyText,
  ImageHint,
  ImagePreviewImg,
  ImagePreviewWrapper,
  ImageUploadButton,
} from './styles';

const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
  imageFile,
  existingPicture,
  isDarkMode,
  onFileSelected,
  onDelete,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);

  const hasImage =
    !!imageFile ||
    (!!existingPicture &&
      existingPicture !== DEFAULT_PICTURE &&
      existingPicture.trim() !== '');

  const imageSrc = imageFile
    ? URL.createObjectURL(imageFile)
    : existingPicture || null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      notification.error('Image Too Large', 'Please select an image smaller than 10 MB.', 5);
      return;
    }
    if (inputRef.current) inputRef.current.value = '';
    setCropSrc(URL.createObjectURL(file));
  };

  return (
    <>
      <ImagePreviewWrapper $isDark={isDarkMode}>
        {hasImage && imageSrc ? (
          <>
            <ImagePreviewImg
              src={imageSrc}
              alt=""
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            <ImageActionButtons>
              <ImageUploadButton onClick={() => inputRef.current?.click()}>
                <SyncOutlined />
                Change image
              </ImageUploadButton>
              <DeleteImageButton
                icon={<DeleteOutlined />}
                onClick={onDelete}
                size="small"
                danger
              />
            </ImageActionButtons>
          </>
        ) : (
          <ImageEmptyState onClick={() => inputRef.current?.click()}>
            <ImageEmptyIcon>
              <PictureOutlined />
            </ImageEmptyIcon>
            <ImageEmptyText>No image selected</ImageEmptyText>
            <ImageUploadButton
              onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
            >
              + Add image
            </ImageUploadButton>
          </ImageEmptyState>
        )}

        <HiddenFileInput
          type="file"
          accept="image/*"
          ref={inputRef}
          onChange={handleFileChange}
        />
      </ImagePreviewWrapper>
      <ImageHint>{IMAGE_FILE_HINT}</ImageHint>

      {cropSrc && (
        <ImageCropModal
          open={!!cropSrc}
          imageSrc={cropSrc}
          onCropDone={(file: File) => {
            setCropSrc(null);
            onFileSelected(file);
          }}
          onCancel={() => setCropSrc(null)}
        />
      )}
    </>
  );
};

export default ImageUploadField;
